import React, { useEffect, useState, useRef } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { useSelector, useDispatch } from 'react-redux';
import cl from 'classnames';
import {
  selectRelatedAnnouncements,
  setSelectedAnnouncementCategoryId,
  setSelectedAnnouncement,
  selectSelectedAnnouncementCategoryId,
} from 'store/pages';
import { Pagination, getTotalPages } from 'components/shared';
import {
  AnnouncementCard,
  IAnnouncement,
  IAnnouncementCategory,
} from 'components/Announcement';
import { isScrolledIntoView } from 'utils';
import styles from './RelatedAnnouncements.module.scss';

const INITIAL_PAGE = 1;
const ANNOUNCEMENTS_PER_PAGE = 4;
export const SCROLL_OFFSET = 50;

const RelatedAnnouncements: React.FC<MappedComponentProperties> = () => {
  const dispatch = useDispatch();
  const relatedAnnouncements = useSelector(selectRelatedAnnouncements);
  const categoryId = useSelector(selectSelectedAnnouncementCategoryId);
  const [page, setPage] = useState(INITIAL_PAGE);
  const rootTagRef = useRef<HTMLDivElement>(null);

  const totalPages = getTotalPages(
    relatedAnnouncements.length,
    ANNOUNCEMENTS_PER_PAGE
  );
  const firstVisibleItem = (page - 1) * ANNOUNCEMENTS_PER_PAGE;
  const visibleItems = relatedAnnouncements.slice(
    firstVisibleItem,
    firstVisibleItem + ANNOUNCEMENTS_PER_PAGE
  );

  useEffect(() => {
    const rootElement = rootTagRef.current as HTMLDivElement;

    if (rootElement && !isScrolledIntoView(rootElement)) {
      const { top } = rootElement.getBoundingClientRect();

      window.scrollTo({
        top: top + window.pageYOffset - SCROLL_OFFSET,
        behavior: 'smooth',
      });
    }
  }, [page]);

  useEffect(() => {
    setPage(INITIAL_PAGE);
  }, [categoryId]);

  if (!relatedAnnouncements.length) {
    return null;
  }

  const handleTitleClick = (announcement: IAnnouncement) => {
    dispatch(setSelectedAnnouncement(announcement));
  };
  const handleCategoryClick = (
    categoryId: IAnnouncementCategory['tagName']
  ) => {
    dispatch(setSelectedAnnouncementCategoryId(categoryId));
  };
  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const announcementsItems = visibleItems.map((announcement) => (
    <li key={announcement.publishDate}>
      <AnnouncementCard
        classes={{
          announcementCard: styles.relatedAnnouncements__card,
          announcementCard__title: styles.relatedAnnouncements__cardTitle,
        }}
        announcement={announcement}
        onCategoryClick={handleCategoryClick}
        onTitleClick={handleTitleClick}
      />
    </li>
  ));

  return (
    <div
      className={cl('margin-bottom-l', styles.relatedAnnouncements)}
      ref={rootTagRef}
    >
      <ul className={styles.relatedAnnouncements__list}>
        {announcementsItems}
      </ul>
      {totalPages > 1 && (
        <Pagination
          className={styles.relatedAnnouncements__pagination}
          onPageChange={handlePageChange}
          currentPage={page}
          totalPages={totalPages}
          visibleAmount={7}
          visibleAmountMobile={5}
        />
      )}
    </div>
  );
};

export default RelatedAnnouncements;
